import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PublicationDisplayNamesContext } from '../../Contexts/PublicationDisplayNames/PublicationDisplayNamesContext'
import { ChargeCodeContext } from '../../Contexts/ChargeCode/ChargeCodeContext'
import { Wrapper } from './DownloadData.styles'
import { Alert, SUCCESS_ALERT_GLOBAL, getAlertUniqueId } from '@mds/mds-reactjs-library'
import AdditionalResources from '../Shared/AdditionalResources'
import DataEndpoints from '../../Services/DataEndpoints'
import { useCreateToast } from '../../Hooks/useCreateToast'
import { hasDatasetSelectorAccess } from '../../Utility/UserUtils'
import DatasetTreeLeft, {
  getDefaultLatestPublication,
  getDefaultLatestPublicationPreferLive,
  buildInitialPublicationByDataset,
  publicationToSelection
} from './DatasetTreeLeft'
import DatasetTreeLeftInternal from './DatasetTreeLeftInternal'
import ApplySection from './ApplySection'
import {
  buildTree,
  getAllAttributeIds,
  getAttributeById,
  getFirstSelectableAttributeId,
  setAttributeTreeExpandDepth,
} from '../../Utility/TreeUtility'

/** Dimensions where only top-level parent rows start expanded; nested parents start collapsed. */
const DOWNLOAD_TOP_LEVEL_EXPAND_ONLY = new Set(['geographies', 'asset classes', 'client segments', 'vehicles', 'years'])

function isDownloadTopLevelExpandOnlyDimension(dimension) {
  const display = (dimension?.DisplayName ?? dimension?.displayName ?? '').trim().toLowerCase()
  const name = (dimension?.Name ?? dimension?.name ?? '').trim().toLowerCase()
  return DOWNLOAD_TOP_LEVEL_EXPAND_ONLY.has(display) || DOWNLOAD_TOP_LEVEL_EXPAND_ONLY.has(name)
}
import SelectionLinks from './SelectionLinks'
import AttributesSelector from './AttributesSelector'
import { useNavigate } from 'react-router-dom'
import { ReportTypes } from '../../Constants/ReportTypes'
import { Source } from '../../Constants/Source'
import ReportingEndpoints from '../../Services/ReportingEndpoints'
import DatasetInfoPopUp from './DatasetInfoPopUp'
import LocalStorage, { Keys } from '../../Storage/LocalStorage'
import { getReleaseDisplayName } from '../../Constants/ReleaseDisplayNames'
import { isChargeCodeRequired } from '../../Utility/ChargeCodeUtility'
import { buildChargeCodeDetailsJson } from '../../Utility/ChargeCodeDetailsPayload'
import { ChargeCodeModal } from '../Modals/ChargeCodeModal/ChargeCodeModal'

/** Same date line as the Versions tab in DatasetInfoPopUp. */
function formatPublicationCreatedAtForHeader(createdDate) {
  if (createdDate == null || createdDate === '') return null
  const d = new Date(createdDate)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' })
}

/** Matches the text shown next to each radio in the Versions tab. */
function formatPublicationVersionTabLabel(pub) {
  if (!pub) return ''
  const label = `${pub.displayName ?? pub.name} (${pub.publicationStatus?.name})`
  const created = formatPublicationCreatedAtForHeader(pub.createdDate)
  return created ? `${label} - ${created}` : label
}

const DownloadData = () => {
  const { state: reUseReport } = useLocation()
  const [datasets, setDatasets] = useState([])
  const [allPublications, setAllPublications] = useState([])
  const [dimensions, setDimensions] = useState([])
  const [selections, setSelections] = useState([])
  const [visibleDimensionIds, setVisibleDimensionIds] = useState([])
  const [selectedDimensionId, setSelectedDimensionId] = useState(null)
  const rightPaneScrollRef = useRef(null)  // ref is attached to inner .dimension in AttributesSelector
  const dimensionAttributesWrapRef = useRef(null)

  const [selectedPublication, setSelectedPublication] = useState(null)
  const [publicationByDataset, setPublicationByDataset] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [showDefinition, setShowDefinition] = useState(false)
  const [showSelectionAppliedNotification, setShowSelectionAppliedNotification] = useState(false)
  const [showDatasetInfo, setShowDatasetInfo] = useState(false)
  const [datasetIdForInfo, setDatasetIdForInfo] = useState(null)
  const [roles, setRoles] = useState([])
  const { createErrorToast } = useCreateToast()
  const isGgcAdmin = roles[0]?.name?.toLowerCase() === 'ggc admin'
  const navigate = useNavigate()
  const { setPublications: setPublicationDisplayNames } = useContext(PublicationDisplayNamesContext)
  const { chargeCode, chargeCodeModal, openChargeCodeModal } = useContext(ChargeCodeContext)

  useEffect(() => {
    Promise.all([
      DataEndpoints.getDatasets(),
      DataEndpoints.getPublications(null, null)
    ])
      .then(([res1, res2]) => {
        const rawDs = res1?.data
        const rawPubs = res2?.data
        const dsList = Array.isArray(rawDs) ? rawDs : (rawDs?.content ?? rawDs?.datasets ?? [])
        const pubList = Array.isArray(rawPubs) ? rawPubs : (rawPubs?.content ?? rawPubs?.publications ?? [])
        // Filter datasets by DataSelectorAccess permission
        const filteredDsList = (dsList || []).filter(ds => hasDatasetSelectorAccess(ds))
        const sortedDs = filteredDsList.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        const pubs = pubList || []
        setDatasets(sortedDs)
        setAllPublications(pubs)
        setPublicationDisplayNames(pubs, isGgcAdmin)
        const initialByDataset = buildInitialPublicationByDataset(sortedDs, pubs, isGgcAdmin)
        const hasReuseDatasetAccess =
          reUseReport?.datasetId != null &&
          sortedDs.some((d) => String(d.datasetId ?? d.id) === String(reUseReport.datasetId))
        if (hasReuseDatasetAccess && reUseReport?.generatedData?.publicationId) {
          const targetPubId = reUseReport.generatedData.publicationId
          const targetDsId = reUseReport.datasetId
          const pub = pubs.find(
            (p) =>
              (p.dataset?.datasetId === targetDsId || p.datasetId === targetDsId) &&
              (p.publicationId === targetPubId || p.id === targetPubId)
          )
          if (pub) {
            const selection = publicationToSelection(pub, isGgcAdmin)
            setPublicationByDataset({
              ...initialByDataset,
              [targetDsId]: selection
            })
            setSelectedPublication(selection)
          } else {
            setPublicationByDataset(initialByDataset)
          }
        } else {
          const defaultPub =
            getDefaultLatestPublicationPreferLive(sortedDs, pubs, isGgcAdmin) ??
            getDefaultLatestPublication(sortedDs, pubs, isGgcAdmin)
          if (defaultPub) {
            setPublicationByDataset({
              ...initialByDataset,
              [defaultPub.datasetId]: defaultPub
            })
            setSelectedPublication(defaultPub)
          } else {
            setPublicationByDataset(initialByDataset)
          }
        }
      })
      .catch((e) => createErrorToast(e))
    // Run once on mount; reUseReport read from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectPublication = (selection) => {
    if (!selection) return
    setSelectedPublication(selection)
    if (selection.datasetId != null) {
      setPublicationByDataset((prev) => ({
        ...prev,
        [selection.datasetId]: selection
      }))
    }
  }

  const activeDatasetId = selectedPublication?.datasetId ?? null
  const activePublicationId = selectedPublication?.publicationId ?? selectedPublication?.id ?? null
  const selectedPublicationIdForActiveDataset =
    activeDatasetId != null
      ? publicationByDataset[activeDatasetId]?.publicationId ?? selectedPublication?.publicationId
      : selectedPublication?.publicationId

  useEffect(() => {
    if (showSelectionAppliedNotification) {
      const timer = setTimeout(() => {
        setShowSelectionAppliedNotification(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showSelectionAppliedNotification])

  useEffect(() => {
    const publicationId = selectedPublication?.publicationId ?? selectedPublication?.id
    if (publicationId == null) {
      setDimensions([])
      setSelections([])
      setVisibleDimensionIds([])
      setSelectedDimensionId(null)
      return
    }
    DataEndpoints
      .getMetadata(publicationId)
      .then((res) => {
        const raw = res?.data
        // console.log('[DownloadData] Metadata API raw response:', raw)
        const metadata = Array.isArray(raw) ? raw : (raw?.content ?? raw?.dimensions ?? [])
        if (!Array.isArray(metadata) || metadata.length === 0) {
          setDimensions([])
          return
        }
        // console.log('[DownloadData] Metadata received (parsed dimensions):', metadata)
        for (let dimension of metadata) {
          const attrs = dimension.Attributes ?? dimension.attributes ?? []
          const topLevelExpandOnly = isDownloadTopLevelExpandOnlyDimension(dimension)
          if (!topLevelExpandOnly) {
            attrs.forEach((attribute) => {
              attribute.IsExpanded = true
            })
          }
          dimension.Attributes = buildTree(attrs)
          if (topLevelExpandOnly) {
            setAttributeTreeExpandDepth(dimension.Attributes, 1)
          }
        }
        setDimensions(metadata)
        if (reUseReport && (reUseReport.generatedData?.publicationId === publicationId || reUseReport.generatedData?.publicationId === String(publicationId))) {
          setSelections(JSON.parse(reUseReport.generatedData.selections || '[]'))
          setShowSelectionAppliedNotification(true)
        } else {
          setSelections(
            metadata?.map((dimension) => {
              const detailsStr = dimension.Details ?? dimension.details ?? '{}'
              const details = typeof detailsStr === 'object' ? detailsStr : JSON.parse(detailsStr)
              const dimId = dimension.DimensionID ?? dimension.dimensionId
              return {
                DimensionID: dimId,
                IsValid: details.singleSelect ? true : false,
                Attributes: details.singleSelect
                  ? [getFirstSelectableAttributeId(dimension)].filter(Boolean)
                  : details.selectAllByDefault
                    ? getAllAttributeIds(metadata.find((dim) => (dim.DimensionID ?? dim.dimensionId) === dimId))
                    : []
              }
            })
          )
        }
        const visibleIds =
          metadata?.filter((d) => {
            const dStr = d.Details ?? d.details ?? '{}'
            const det = typeof dStr === 'object' ? dStr : JSON.parse(dStr)
            return det.visibleByDefault
          }).map((d) => d.DimensionID ?? d.dimensionId) || []
        setVisibleDimensionIds(visibleIds)
        if (visibleIds.length > 0) setSelectedDimensionId(visibleIds[0])
      })
      .catch((e) => createErrorToast(e))
    // Only re-fetch when selected publication changes; createErrorToast omitted to avoid re-running every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPublication?.publicationId ?? selectedPublication?.id])

  // Load user role for sidebar variant (internal user vs dropdown)
  useEffect(() => {
    const rolesRaw = LocalStorage.get(Keys.ROLES)
    const parsed = Array.isArray(rolesRaw) ? rolesRaw : (typeof rolesRaw === 'string' ? (() => { try { return JSON.parse(rolesRaw) } catch { return [] } })() : [])
    // console.log('[DownloadData] User role information:', { rolesRaw, parsed })
    setRoles(Array.isArray(parsed) ? parsed : [])
  }, [])

  // Rebuild publication display names map when admin flag or publications change (admin uses API names, non-admin uses hardcoded)
  useEffect(() => {
    if (allPublications.length > 0) setPublicationDisplayNames(allPublications, isGgcAdmin)
  }, [isGgcAdmin, allPublications, setPublicationDisplayNames])

  // Scroll right pane to top when switching dimensions
  useEffect(() => {
    if (rightPaneScrollRef.current) rightPaneScrollRef.current.scrollTop = 0
    if (dimensionAttributesWrapRef.current) dimensionAttributesWrapRef.current.scrollTop = 0
  }, [selectedDimensionId])

  const setSelection = (dimensionId, attributeIds) => {
    setSelections((prev) =>
      prev.map((s) =>
        s.DimensionID === dimensionId ? { ...s, Attributes: attributeIds } : s
      )
    )
  }

  const generateAndGoToLibrary = (e, reportExtra = {}) => {
    const button = e?.target
    if (button) {
      button.disabled = true
    }

    const defaultName = `GGC-${selectedPublication?.displayName}_${new Date().toLocaleString('en-US', { hour12: false }).replace(',', '')}`;
    const payload = {
      datasetId: selectedPublication?.datasetId,
      publicationId: selectedPublication?.publicationId,
      reportStatusId: 1,
      name: defaultName,
      sourceName: Source.DATASET_DOWNLOAD,
      reportTypeName: ReportTypes.Default,
      selections: JSON.stringify(selections),
      placeDataGenerationRequest: true
    }
    if (chargeCode?.value) {
      payload.chargeCode = chargeCode.value
    }
    if (reportExtra?.details) {
      payload.details = reportExtra.details
    } else if (isChargeCodeRequired() && chargeCode?.value) {
      payload.details = buildChargeCodeDetailsJson({
        chargeCodeValue: chargeCode.value,
        eDEmail: chargeCode.ed_email || '',
        isIAmEDChecked: chargeCode.isIAmEDChecked,
        isAgreementPolicyChecked: chargeCode.isAgreementPolicyChecked,
        isWarRoomChecked: chargeCode.isWarRoomChecked
      })
    }
    ReportingEndpoints.createReportRequestWithSelections(payload)
      .catch((err) => createErrorToast(err))
      .finally(() => {
        if (button) button.disabled = false
        navigate('/Library', { state: { selectedReleaseDisplayName: selectedPublication?.displayName } })
      })
  }

  const handleGenerateClick = (e) => {
    if (isChargeCodeRequired()) {
      openChargeCodeModal({
        actionLabel: 'Generate',
        datasetId: selectedPublication?.datasetId ?? null,
        pendingAction: (reportExtra) => generateAndGoToLibrary(null, reportExtra)
      })
    } else {
      generateAndGoToLibrary(e)
    }
  }

  const selectedDimension = dimensions.find((d) => d.DimensionID === selectedDimensionId)
  const selectedSelection = selections.find((s) => s.DimensionID === selectedDimensionId)

  return (
    <>
      {chargeCodeModal.isOpen && <ChargeCodeModal />}
      <Wrapper>
        {showSelectionAppliedNotification && (
          <Alert
            id={getAlertUniqueId()}
            type={SUCCESS_ALERT_GLOBAL}
            style={{ marginBottom: '12px', background: '#117e1a' }}
            onCloseClick={() => setShowSelectionAppliedNotification(false)}
          >
            Selection applied!
          </Alert>
        )}

        {showDatasetInfo && (
          <DatasetInfoPopUp
            setShowDatasetInfo={() => {
              setShowDatasetInfo(false)
              setDatasetIdForInfo(null)
            }}
            datasets={datasets}
            allPublications={allPublications}
            datasetId={datasetIdForInfo ?? selectedPublication?.datasetId}
            selectedPublicationId={selectedPublicationIdForActiveDataset}
            publicationByDataset={publicationByDataset}
            isGgcAdmin={isGgcAdmin}
            releaseDisplayName={selectedPublication?.displayName}
            onApplyPublicationOnClose={(publicationId, datasetId) => {
              const targetDatasetId = datasetId ?? datasetIdForInfo ?? selectedPublication?.datasetId
              if (targetDatasetId == null) return
              const pub = allPublications.find(
                (p) =>
                  String(p.publicationId ?? p.id) === String(publicationId) &&
                  String(p.dataset?.datasetId ?? p.datasetId) === String(targetDatasetId)
              )
              if (!pub) return
              const selection = publicationToSelection(pub, isGgcAdmin)
              setPublicationByDataset((prev) => ({
                ...prev,
                [targetDatasetId]: selection
              }))
              if (String(targetDatasetId) === String(activeDatasetId)) {
                setSelectedPublication(selection)
              }
            }}
          />
        )}

        {/* Left pane: dataset tree; internal user sees preselected row per parent, others see dropdown */}
        <div className="left-sidebar">
          <div className="left-sidebar-tree-wrap">
            <div className="left-sidebar-dataset-tree">
              {isGgcAdmin ? (
                <DatasetTreeLeft
                  datasets={datasets}
                  allPublications={allPublications}
                  activeDatasetId={activeDatasetId}
                  activePublicationId={activePublicationId}
                  publicationByDataset={publicationByDataset}
                  isGgcAdmin={isGgcAdmin}
                  onSelectPublication={selectPublication}
                  onDatasetInfoClick={(pub, datasetId) => {
                    const id = datasetId ?? pub.dataset?.datasetId ?? pub.datasetId
                    setDatasetIdForInfo(id)
                    selectPublication(publicationToSelection(pub, isGgcAdmin))
                    setShowDatasetInfo(true)
                  }}
                />
               
              ) : (
                <DatasetTreeLeftInternal
                datasets={datasets}
                allPublications={allPublications}
                activeDatasetId={activeDatasetId}
                activePublicationId={activePublicationId}
                publicationByDataset={publicationByDataset}
                onSelectPublication={selectPublication}
                onDatasetInfoClick={(pub, datasetId) => {
                  const id = datasetId ?? pub.dataset?.datasetId ?? pub.datasetId
                  setDatasetIdForInfo(id)
                  selectPublication(publicationToSelection(pub, false))
                  setShowDatasetInfo(true)
                }}
              />
              )}
            </div>
          </div>
        </div>

        {/* Main content: Select data (dimensions + attributes) or empty state */}
        <div className="main-content">
          {selectedPublication ? (
            <>
              {/* {(() => {
                const pubId = selectedPublication?.publicationId ?? selectedPublication?.id
                const pub = pubId != null
                  ? allPublications.find((p) => String(p.publicationId ?? p.id) === String(pubId))
                  : null
                const ds = datasets.find(
                  (d) => (d.datasetId ?? d.id) === selectedPublication.datasetId
                )
                const datasetName = ds?.displayName ?? ds?.name ?? selectedPublication.displayName ?? ''
                const versionLine = pub
                  ? formatPublicationVersionTabLabel(pub)
                  : (selectedPublication.displayName ?? '')
                if (!datasetName && !versionLine) return null
                const fullTitle = [datasetName, versionLine].filter(Boolean).join(' — ')
                return (
                  <div className="dataset-context-header" aria-live="polite">
                    {datasetName && (
                      <h2 className="dataset-context-name" title={fullTitle}>{datasetName}</h2>
                    )}
                    {versionLine && (
                      <span className="dataset-context-version" title={fullTitle}>
                        {versionLine}
                      </span>
                    )}
                  </div>
                )
              })()} */}
              <div className="select-data-layout">
                {/* Left: Select data heading + dimension nav list */}
                <div className="select-data-left">
                  {/* <h2 className="select-data-heading">Select data</h2> */}
                  <div className="dimension-nav">
                    <ul className="dimension-nav-list">
                      {dimensions.map((dim) => {
                        const sel = selections.find((s) => s.DimensionID === dim.DimensionID)
                        const totalIds = getAllAttributeIds(dim)
                        const totalCount = totalIds.length
                        const selectedCount = sel?.Attributes?.length ?? 0
                        const details = dim?.Details ? JSON.parse(dim.Details) : {}
                        const isSingleSelect = details.singleSelect === true
                        const selectedAttributeId = isSingleSelect && selectedCount ? sel.Attributes[0] : null
                        const selectedAttribute = selectedAttributeId != null ? getAttributeById(dim, selectedAttributeId) : null
                        const countLabel = isSingleSelect && selectedAttribute
                          ? `(${selectedAttribute.DisplayName})`
                          : `(${selectedCount}/${totalCount} selected)`
                        return (
                          <li key={dim.DimensionID}>
                            <button
                              type="button"
                              className={`dimension-nav-item ${
                                selectedDimensionId === dim.DimensionID ? 'active' : ''
                              }`}
                              onClick={() => {
                                setSelectedDimensionId(dim.DimensionID)
                                setSearchTerm('')
                              }}
                            >
                              <span className="dimension-nav-label">{dim.DisplayName}</span>
                              <span className="dimension-nav-count">
                                {countLabel}
                              </span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                  <ApplySection selections={selections} onGenerate={handleGenerateClick} />
                </div>

                {/* Right: filter section (links) + dimension attributes tree */}
                <div className="select-data-right">
                  {selectedDimensionId && selectedDimension && (
                    <>
                      <div className="filter-section">
                        {/* <div className="searchBoxContainer">
                          <label htmlFor="download-data-search">
                            <input
                              id="download-data-search"
                              type="search"
                              placeholder="Search"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                              aria-label="Search datapoints"
                            />
                            {!searchTerm ? (
                              <span className="search-icon" aria-hidden>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="11" cy="11" r="8" />
                                  <path d="m21 21-4.35-4.35" />
                                </svg>
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="search-clear"
                                onClick={() => setSearchTerm('')}
                                aria-label="Clear search"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 6 6 18" />
                                  <path d="m6 6 12 12" />
                                </svg>
                              </button>
                            )}
                          </label>
                        </div> */}
                        <SelectionLinks
                          dimension={selectedDimension}
                          selection={selectedSelection}
                          setSelection={setSelection}
                          selectedDimensionId={selectedDimensionId}
                          showDefinition={showDefinition}
                          setShowDefinition={setShowDefinition}
                        />
                      </div>
                      <div className="dimension-attributes" ref={dimensionAttributesWrapRef}>
                        {selectedDimension && selectedSelection && (
                          <AttributesSelector
                            dimension={selectedDimension}
                            selection={selectedSelection}
                            setSelection={setSelection}
                            searchTerm={searchTerm}
                            showDefinition={showDefinition}
                            scrollContainerRef={rightPaneScrollRef}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="select-data-empty">
              <p>Select a dataset from the left pane to configure and save your selection.</p>
            </div>
          )}
          {/* <div className="additional-resources-wrap">
            <AdditionalResources />
          </div> */}
        </div>
      </Wrapper>
    </>
  )
}

export default DownloadData
